import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Row, Col, Typography, Button, Result, Space, Tag, Image,
  Modal, Form, Input, App,
} from 'antd';
import {
  WhatsAppOutlined, LeftOutlined, RightOutlined, ArrowLeftOutlined,
  ExpandOutlined, TrophyOutlined, CheckCircleFilled,
} from '@ant-design/icons';
import PujaSpinner from '../../components/PujaSpinner';
import { SiteHeader, SiteFooter } from './RematePage';
// El arbol vive en AnimalPage —la ficha del remate— y no en RematePage, que es
// de donde salen el header y el footer. Son dos archivos distintos.
import { ArbolGenealogico, CarruselMedia } from './AnimalPage';
import { tipografiaDe, altoHeaderDe, NEUTROS as T } from '../../utils/micrositioTema';
import { fixUrl, catalogoPublicoService, analyticsService } from '../../services/api';
import { useAvisarAltoAlPadre } from '../../hooks/useAvisarAltoAlPadre';
import { linksDelProductor, seccionesDelCatalogo } from '../../utils/menuCatalogo';
import { escalonDe } from '../../utils/depsEscalones';
import { clickInterno } from '../../utils/navegacionSpa';
import BarraBienvenida from './components/BarraBienvenida';
import { useAltoHeaderMedido } from './useAltoHeaderMedido';

// El verde de WhatsApp, el mismo que usa la ficha del remate
// (`AnimalPage.jsx`) en su boton de compartir. Va FIJO y no sale de `acento`:
// el acento es la identidad del productor, y este boton no representa al
// productor sino a WhatsApp. Un comprador reconoce ese verde antes de leer la
// etiqueta — es justamente lo que lo hace un atajo y no un boton mas.
const VERDE_WHATSAPP = '#25D366';

const { Title, Text, Paragraph } = Typography;

const ESTILOS = `
/* LAS TARJETAS DE INFORMACION SE DIBUJAN DOS VECES y este par de reglas decide
   cual de las dos se ve. La otra copia vive en un <Col xs={0}>, que antd SI
   RENDERIZA: su Col siempre crea el div (antd/es/grid/col.js, nunca devuelve
   null) y xs={0} solo agrega la clase ant-col-xs-0, que es display:none. O sea
   columnaInfo esta montado DOS VECES en el DOM y se oculta por CSS, no se
   desmonta. Visualmente se ve una sola copia a cualquier ancho, pero el que
   meta un useEffect, un fetch o un <video> ahi adentro va a verlo correr dos
   veces en escritorio. Mismo mecanismo que .animal-info-mobile en la ficha del
   remate; el corte en 991px es el mismo lg de antd.
   SIN COMILLAS INVERSAS ACA: esto vive dentro de un template literal de JS y
   la primera lo cierra, rompiendo el archivo entero. */
.pj-info-mobile { display: none; }
@media (max-width: 991px) {
  .pj-info-mobile { display: block; }
}

/* SIN COMILLAS INVERSAS ACA: esto vive dentro de un template literal de JS y la
   primera cierra la cadena, rompiendo el archivo entero. Ya paso dos veces.

   Las cinco declaraciones son las MISMAS que .pj-pieza en BloqueToro, y esa
   duplicacion queda anotada como deuda: el camino bueno es izar una sola regla
   compartida, como ya se hizo con los tokens en utils/micrositioTema.js.
   Lo que SI se corrige aca es la incoherencia de adentro: el fondo estaba
   hardcodeado en #fff mientras las tres lineas de abajo salian de T, y
   T.surface ES '#ffffff'. Dos convenciones dentro de una misma regla de cinco
   lineas es exactamente como empezo la divergencia que micrositioTema vino a
   cerrar. */
.pj-ficha-pieza {
  background: ${T.surface};
  border: 1px solid ${T.border};
  border-radius: ${T.radius}px;
  box-shadow: ${T.shadowSm};
  overflow: hidden;
}
`;


/**
 * Una fila de DEP: valor, barra de percentil y precisión.
 *
 * OJO CON LA DIRECCIÓN: `percentil` es el rank oficial de la asociación y **1
 * es el mejor, 100 el peor** — verificado contra los datos de producción. La
 * barra se llena con `100 - percentil`; proporcional al número mostraría a los
 * mejores toros como los peores.
 */
const FilaDep = ({ dep, color }) => {
  const conRank = dep.percentil != null;
  const lleno = conRank ? Math.max(0, Math.min(100, 100 - dep.percentil)) : 0;
  // El escalón sale de `utils/depsEscalones.js`, que es la fuente única de la
  // escala: los cortes, los colores y el POR QUÉ de la paleta viven allá.
  const escalon = conRank ? escalonDe(dep.percentil) : null;

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
        <Text style={{ fontSize: 13 }}>{dep.etiqueta}</Text>
        <Text style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
          {/* `!= null` para que un DEP de 0 —la línea base de la raza— se
              muestre en vez de caerse. */}
          <Text strong style={{ fontSize: 14 }}>
            {dep.valor != null ? dep.valor : '—'}
          </Text>
          {/* EL DATO QUE ANTES SE PERDÍA. Iba con el gris secundario de antd a
              11px —"una escala de grises que por ahí se pierde", textual del
              cabañero— y es el más accionable de la fila: el valor del DEP sin
              saber dónde cae en la raza no se puede juzgar.

              Dentro de escalón: MAYÚSCULAS, peso 700 y el color de su escalón.
              Fuera de escalón (pasado el 30%): queda como estaba, gris y en
              minúscula. Ese contraste de TRATAMIENTO es la mitad del realce —
              si se destacaran los diez, no se destacaría ninguno. */}
          {/* EL ORDEN ES DEP, PRECISION Y RANK. La precision califica al
              numero que acaba de leerse —cuanta confianza tiene ESE valor— asi
              que va pegada a el; el rank ubica al animal en la raza y cierra la
              fila, que es el dato con el que uno se queda. Antes la precision
              iba ultima y separaba el valor de su propio respaldo. */}
          {/* "1.7  –  Prec. 0.73  –  Rank 97%", IGUAL que `FilaDepFicha` en la
              ficha del remate (AnimalPage.jsx): precisión y rank a 13 y en
              color de texto, no a 11 y en gris (feedback de Facundo,
              2026-10-15). El DEP sigue a 14 negrita y el rank en su chip de
              escalón —son el dato y la señal—; los guiones con aire
              reemplazan al punto medio. Son gemelos a propósito: un DEP tiene
              que leerse igual en el remate y en el catálogo. */}
          {dep.precision != null && (
            <>
              <Text type="secondary" style={{ margin: '0 10px' }}>–</Text>
              <Text style={{ fontSize: 13 }}>Prec. {dep.precision}</Text>
            </>
          )}
          {conRank && (
            <>
              <Text type="secondary" style={{ margin: '0 10px' }}>–</Text>
              {escalon ? (
                <Text
                  strong
                  style={{
                    fontSize: 13,
                    color: escalon.color,
                    // Sólo el escalón de arriba lleva chip. El `fondo` viene en
                    // null para los otros dos, y sin fondo tampoco va el padding:
                    // un chip sin color de fondo es un texto corrido con aire.
                    ...(escalon.fondo
                      ? { background: escalon.fondo, padding: '2px 7px', borderRadius: 4 }
                      : {}),
                  }}
                  // El nombre del escalón NO se dibuja para no ocupar lugar, pero
                  // va en el `title`: quien pasa el mouse lee "Élite" y entiende
                  // la escala sin que haga falta una leyenda en la página.
                  title={`${escalon.etiqueta} — rank ${dep.percentil}% de la raza`}
                >
                  Rank {dep.percentil}%
                </Text>
              ) : (
                <Text style={{ fontSize: 13 }}>Rank {dep.percentil}%</Text>
              )}
            </>
          )}
        </Text>
      </div>
      {conRank ? (
        <div style={{ height: 6, background: T.pearl, borderRadius: 3, overflow: 'hidden' }}>
          <div
            style={{
              width: `${lleno}%`, height: '100%', borderRadius: 3,
              // Dentro de escalón, la barra toma su color. Fuera, se queda con
              // el ACENTO DEL PRODUCTOR y no con un gris: realzar necesita una
              // línea de base de la que sobresalir, y si los DEPs flojos
              // salieran grises, un toro sin nada en el top 30% mostraría una
              // card entera apagada que se lee como un error de carga y no
              // como lo que es. El largo de la barra ya cuenta esa historia.
              background: escalon ? escalon.color : color,
            }}
          />
        </div>
      ) : (
        // Sin percentil NO se dibuja una barra vacía: una barra en cero se lee
        // como "el peor de la raza", y lo que pasa es que no se sabe.
        <Text type="secondary" style={{ fontSize: 11 }}>sin percentil</Text>
      )}
    </div>
  );
};


// EN QUÉ ORDEN SE LEEN LAS MEDIDAS, y es decisión de acá por una razón dura:
// Flask serializa con `sort_keys=True`, así que el dict `medidas` llega
// ALFABÉTICO —"Área de ojo de bife" primero, "Peso" sexto— por más que el
// backend lo escriba en otro orden. El comprador las lee al revés: los dos
// pesos primero (el de nacer predice facilidad de parto, es el primer número
// que mira), después la circunferencia escrotal, y al final lo de carne.
const ORDEN_MEDIDAS = [
  // Los pesos en orden de la CURVA DE CRECIMIENTO, que es como los lee un
  // cabañero: nacimiento, destete, 18 meses, hoy. El backend no puede imponer
  // este orden aunque arme el dict asi: `app.json.sort_keys` esta en True y
  // Flask lo serializa alfabetico, con lo cual "Peso" termina antes que "Peso
  // al nacer". El orden es responsabilidad de quien dibuja.
  'Peso al nacer',
  'Peso al destete',
  'Peso a 18 meses',
  'Peso',
  'Circunf. escrotal',
  'Área de ojo de bife',
  'Espesor grasa dorsal',
  'Grasa intramuscular',
  'Frame',
];

// Una medida que el backend agregue y acá no esté listada va al FINAL, no
// desaparece: el orden es una preferencia, no una lista blanca.
const posicionMedida = (etiqueta) => {
  const i = ORDEN_MEDIDAS.indexOf(etiqueta);
  return i === -1 ? ORDEN_MEDIDAS.length : i;
};


const Dato = ({ etiqueta, valor }) => {
  if (valor == null || valor === '') return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em',
          textTransform: 'uppercase', color: T.textDim,
        }}
      >
        {etiqueta}
      </div>
      <div style={{ fontSize: 15.5 }}>{valor}</div>
    </div>
  );
};


const AnimalCatalogoPage = () => {
  // Le avisa al sitio incrustante cuánto medimos, para que agrande el iframe en
  // vez de dejarlo clavado en los 800px del snippet. Acá importa doble: pasar
  // de un animal a otro con las flechas NO desmonta el componente, así que sin
  // este hook el alto nunca se vuelve a informar y la ficha más larga queda
  // recortada. También pone `data-embed` en el `<html>`, que es lo que activa
  // `html[data-embed] .micrositio-page` en `index.css` —de ahí la clase en el
  // `<div>` de abajo.
  useAvisarAltoAlPadre();

  const { slug, animalSlug } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Un 404 y un 500 NO son la misma pantalla. Ver el `catch` de abajo.
  const [error, setError] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  // El visor a pantalla completa de las fotos del animal.
  //
  // Se controla desde afuera con `items` en vez de envolver el carrusel en un
  // `PreviewGroup`: `CarruselMedia` dibuja `<img>` planos, no el `<Image>` de
  // antd, así que envolverlo NO haría nada — el grupo sólo junta los `Image`
  // que encuentra entre sus hijos. Y modificar el carrusel para que use
  // `Image` tocaría la ficha del remate, que está en producción.
  const [verFotos, setVerFotos] = useState(false);
  // DOS ESTADOS Y NO UNO, y esto arregla un desincronizado real.
  //
  // `fotoActual` es la verdad DEL CARRUSEL; `fotoVisor` es la del visor. Antes
  // habia uno solo haciendo los dos trabajos: se pasaba como `current` del
  // visor y el `onChange` del visor escribia de vuelta en el. Entonces pasar de
  // foto ADENTRO del visor movia el indice del carrusel sin mover el carrusel,
  // y al reabrir "Ver en grande" se abria una foto distinta de la que estaba en
  // pantalla — mirabas la 1 y se abria la 4.
  //
  // Se copia al abrir y no se vuelve a tocar: el visor arranca SIEMPRE en lo
  // que el comprador esta viendo, navegue lo que navegue adentro.
  const [fotoVisor, setFotoVisor] = useState(0);
  // Que foto esta mirando el comprador en el carrusel, para que "Ver en grande"
  // abra ESA y no siempre la primera. Cuando el slide actual es el video o la
  // foto de un familiar el carrusel avisa `null` y acá se CONSERVA la ultima
  // foto vista: es lo que el visor puede mostrar, porque su lista son
  // `animal.imagenes` y nada mas.
  const [fotoActual, setFotoActual] = useState(0);

  // ── Modal "Consultar por este animal" ───────────────────────────────────
  const [formConsulta] = Form.useForm();
  const [modalConsultaAbierto, setModalConsultaAbierto] = useState(false);
  const [enviandoConsulta, setEnviandoConsulta] = useState(false);
  // `null` = el form; con valor = el estado de éxito (ya no se puede volver
  // atrás al form, sólo cerrar). Distingue whatsapp — donde además hay que
  // ofrecer el link por si el navegador bloqueó el `window.open` — de
  // interna, que sólo confirma que quedó registrada.
  const [consultaEnviada, setConsultaEnviada] = useState(null);

  const cerrarModalConsulta = () => {
    if (enviandoConsulta) return;
    setModalConsultaAbierto(false);
    setConsultaEnviada(null);
    formConsulta.resetFields();
  };

  useEffect(() => {
    let vivo = true;
    setLoading(true);
    // Al pasar de un animal a otro con las flechas el componente NO se
    // desmonta: sin este reset se ve la ficha anterior mientras carga la nueva,
    // y el comprador lee los DEPs del toro que ya dejó atrás.
    setData(null);
    // Se limpia al cambiar de animal, y aca importa mas que en la portada:
    // pasar de uno a otro con las flechas NO desmonta el componente, asi que
    // un error viejo quedaria tapando al animal que si carga.
    setError(null);
    // Y el indice de la foto, POR EL MISMO MOTIVO que los dos de arriba.
    // `onCambioFoto` solo lo mueve la interaccion del usuario —`goTo` y
    // `beforeChange` de `CarruselMedia`—, nunca un cambio de animal, asi que
    // el indice sobrevivia al salto. Estabas en la foto 4 de un toro con 5,
    // pasabas a uno con 2, apretabas "Ver en grande" y el visor abria un
    // indice que no existe. En un catalogo de genetica la foto ES el
    // argumento: es el momento exacto en el que no se puede fallar.
    setFotoActual(0);
    (async () => {
      try {
        const res = await catalogoPublicoService.getAnimal(slug, animalSlug);
        if (vivo) setData(res.data);

        // La visita a la ficha. Va DESPUES del fetch exitoso a proposito: un
        // 404 no es una visita, y contarlo inflaria la metrica justo con los
        // links rotos.
        //
        // catch vacio a proposito: el tracking es fire-and-forget. Si falla
        // analytics, la persona igual tiene que poder ver la ficha.
        const idCatalogo = res.data?.catalogo?.id_catalogo_genetica;
        const idAnimal = res.data?.animal?.id_animal;
        if (vivo && idCatalogo && idAnimal) {
          analyticsService.trackCatalogo('visita_animal', idCatalogo, {
            id_animal: idAnimal,
          }).catch(() => {});
        }
      } catch (err) {
        // SIN `message.error` a proposito: no es una accion puntual que falla,
        // es la pantalla ENTERA la que no carga, y eso se comunica con el
        // `<Result>` a pagina completa de mas abajo. Un toast que se va en tres
        // segundos arriba de eso es ruido duplicado. Excepcion (d) de las
        // reglas del front.
        //
        // PERO EL ERROR NO SE TIRA A LA BASURA. Un 404 aca es el caso NORMAL
        // —el catalogo apagado, el link mal escrito, o un animal que el
        // productor dejo de mostrar— y un 500 es otra cosa completamente.
        // Colapsados en un solo mensaje, una caida del backend se leia como
        // "este animal no existe". Misma distincion que la portada y que
        // `RematePage`.
        if (vivo) {
          setData(null);
          setError(err.response?.status === 404 ? 'not_found' : 'server_error');
        }
      } finally {
        if (vivo) setLoading(false);
      }
    })();
    return () => { vivo = false; };
  }, [slug, animalSlug]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // El alto REAL de la cabecera, medido — compartido con `CatalogoPage` y
  // `SeccionCatalogoPage`. Ver el porqué completo en `useAltoHeaderMedido.js`.
  const altoHeaderMedido = useAltoHeaderMedido(data);

  // `T.surface` y no '#fff': es la MISMA regla que este archivo escribe arriba
  // para `.pj-ficha-pieza`. Escribirla en el bloque de estilos y despues
  // hardcodear el blanco cuatrocientas lineas mas abajo deja las dos
  // convenciones conviviendo igual.
  if (loading) return <PujaSpinner size="lg" label="Cargando ficha…" fondo={T.surface} />;

  if (!data) {
    // El boton de volver se mantiene en los DOS casos: es la unica salida que
    // tiene alguien que entro por un link compartido y no conoce el sitio.
    const volver = (
      <Button onClick={() => navigate(`/genetica/${slug}`)}>Volver al catálogo</Button>
    );
    return error === 'server_error' ? (
      <Result
        status="500"
        title="No pudimos cargar este animal"
        subTitle="Intentá de nuevo en unos momentos."
        extra={volver}
      />
    ) : (
      <Result
        status="404"
        title="No encontramos este animal"
        subTitle="El enlace puede ser incorrecto, o la cabaña dejó de mostrarlo."
        extra={volver}
      />
    );
  }

  const { animal, anterior, siguiente } = data;
  const config = data.catalogo.configuracion_micrositio || {};
  const tipografia = tipografiaDe(config);
  const altoHeader = altoHeaderMedido || altoHeaderDe(config);
  const acento = config.color_primary || '#1a5f2a';
  const conCabecera = config.cabecera_on !== false;
  const conPie = config.footer_on !== false;
  // La franja de bienvenida de OP-1 vive DENTRO de la cabecera en las TRES
  // páginas del catálogo, no sólo en la portada — ver el porqué completo en
  // `CatalogoPage.jsx`, donde también se explica por qué antes NUNCA se veía.
  // `|| 'op1'` es para un catálogo servido por una API vieja: el endpoint de
  // la ficha manda `plantilla_index` desde el 2026-09-22 (antes solo la
  // portada lo hacía, y un catálogo OP-2 mostraba la franja igual acá).
  const plantilla = data.catalogo.plantilla_index || 'op1';
  const franjaBienvenida = plantilla === 'op1'
    ? <BarraBienvenida config={config} tipografia={tipografia} variante="franja" />
    : null;

  const logoCrudo = config.logo_img || data.identidad?.logo_url || null;
  const logoUrl = logoCrudo ? fixUrl(logoCrudo) : null;
  const comoEvento = { nombre_evento: data.catalogo.nombre };

  // `|| 'whatsapp'` porque un catálogo servido por una API vieja no trae
  // `canal_consultas` todavía: se comporta como venía comportándose, que es
  // whatsapp. Con canal whatsapp y SIN número no hay a dónde mandar la
  // consulta, así que el botón sigue sin mostrarse — igual que hoy.
  const canalConsultas = data.catalogo.canal_consultas || 'whatsapp';
  const mostrarConsultar = canalConsultas === 'interna'
    || Boolean(data.catalogo.whatsapp_consultas);

  const irAnimal = (vecino) => navigate(`/genetica/${slug}/animal/${vecino.slug}`);

  // COMPARTIR POR WHATSAPP, como en la ficha del remate.
  //
  // NO se reusa `getShareUrl`: esa arma URLs de REMATE —`eventoSlug` y el
  // endpoint de unfurl de eventos— y aca la ruta publica es otra. Reusarla
  // habria mandado al comprador a un link que no existe.
  //
  // Y SE COMPARTE `window.location.href` SIEMPRE, aunque `configuracion_micrositio`
  // tenga las claves `embed_externo` y `url_embed_publico`. En el remate la
  // variante con el dominio del productor funciona porque el snippet que el
  // pega en su sitio lee el `?animal=` del padre y lo postea al iframe. Para
  // `/genetica/*` ese snippet NO EXISTE, ninguna pagina escucha
  // `navigate-animal`, y el panel ni siquiera ofrece prender `embed_externo`.
  // Armar el link igual seria mandar al comprador a la portada del productor
  // con el animal perdido en silencio. Cuando exista el snippet del catalogo,
  // este es el lugar — y ahi la decision va a `utils/`, junto a `urlMicrositio`
  // y `shareUrls`, que ya la toman cada uno por su lado.
  const compartir = () => {
    const url = window.location.href;
    const nombre = (animal.nombre_corto || animal.nom_animal || '').trim();
    const texto = `Mirá este animal de ${data.catalogo.nombre}:
${nombre}

${url}`;
    // Dominio FIJO, asi que no necesita lista blanca — pero el `noopener` si:
    // no depende de a donde se va, sino de que la pestaña nueva no pueda tocar
    // `window.opener` y redirigir ESTA pagina.
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank', 'noopener,noreferrer');
    // catch vacio a proposito, igual que en el track de visita: el envio a
    // WhatsApp ya se disparo. Un error de analytics no puede molestar a nadie.
    if (data.catalogo?.id_catalogo_genetica) {
      analyticsService.trackCatalogo(
        'compartir_animal', data.catalogo.id_catalogo_genetica,
        { id_animal: animal.id_animal },
      ).catch(() => {});
    }
  };

  // CONSULTAR POR ESTE ANIMAL.
  //
  // Se registra en el backend ANTES de abrir WhatsApp, y en los DOS canales.
  // Con canal whatsapp, si se abriera WhatsApp directo (como hacía antes este
  // botón) y el comprador nunca llega a escribir, la cabaña no se entera de
  // que preguntaron. Registrando primero, el productor ve la consulta en
  // «Consultas del catálogo» exista o no el mensaje de WhatsApp.
  //
  // El mensaje de WhatsApp lleva el NOMBRE del comprador (no sólo el animal):
  // la cabaña recibe el chat ya sabiendo quién escribe, sin tener que
  // preguntar "¿con quién hablo?" antes de poder contestar la consulta real.
  const enviarConsulta = async (values) => {
    setEnviandoConsulta(true);
    try {
      const nombre = values.nombre.trim();
      const mensaje = values.mensaje?.trim() || null;
      const res = await catalogoPublicoService.consultar(slug, {
        id_animal: animal.id_animal,
        nombre,
        telefono: values.telefono.trim(),
        mensaje,
      });
      const canal = res.data?.canal || canalConsultas;

      if (canal === 'whatsapp' && data.catalogo.whatsapp_consultas) {
        const numero = data.catalogo.whatsapp_consultas.replace(/\D/g, '');
        const texto = `Hola, soy ${nombre}. Consulto por ${animal.nombre_corto}.`
          + (mensaje ? `\n${mensaje}` : '');
        const url = `https://wa.me/${numero}?text=${encodeURIComponent(texto)}`;
        // Se intenta abrir WhatsApp y SIEMPRE se muestra la confirmación con
        // el link a mano. No se puede saber si la pestaña se abrió: con
        // `noopener`, `window.open` devuelve `null` por especificación, se
        // haya abierto o no, así que usar el retorno como detector era
        // código muerto. Y DESPUES de un `await` el navegador puede bloquear
        // la pestaña igual (el gesto del usuario ya venció). La consulta ya
        // quedó registrada antes de esta línea, así que ninguno de los dos
        // casos es un error: si WhatsApp abrió, el link sobra; si no, salva.
        window.open(url, '_blank', 'noopener,noreferrer');
        setConsultaEnviada({ canal: 'whatsapp', urlWhatsapp: url });
      } else {
        setConsultaEnviada({ canal: 'interna' });
      }
    } catch (err) {
      message.error(err.response?.data?.error || 'No pudimos enviar la consulta');
    } finally {
      setEnviandoConsulta(false);
    }
  };

  const g = animal.genealogia;
  const hayGenealogia = Boolean(
    g && (g.padres?.padre || g.padres?.madre
      || g.abuelos?.paterno?.abuelo || g.abuelos?.materno?.abuelo),
  );

  // EL MISMO MENU QUE LA PORTADA. Antes la ficha mostraba un solo link
  // —"Volver al catálogo"— y el comprador que entraba directo por un link
  // compartido se quedaba sin navegación: para ver las hembras tenía que
  // adivinar que primero hay que volver.
  //
  // Cada sección es un DESPLEGABLE, no un link: `SiteHeader` la dibuja con
  // `dropdownNav`, que es un `<Dropdown>` de hover sobre un `<span>` sin
  // `onClick`. O sea que el título de la sección no navega a ningún lado —
  // navegan sus HIJOS, y cada uno va derecho a la ficha de ese animal, no a la
  // portada. Es a propósito: desde acá, ofrecer los animales de la sección de
  // a uno es más corto que mandar al comprador de vuelta a la portada.
  // `data.menu` ya viene del backend con la forma que el util espera.
  const navSecciones = seccionesDelCatalogo(
    data.menu,
    // `destino` y no `animalSlug`: ese nombre ya lo tiene el `useParams()` de
    // arriba —el animal que se esta MIRANDO— y sombrearlo hace que el proximo
    // que lea esta linea crea que navega al animal actual.
    (destino) => navigate(`/genetica/${slug}/animal/${destino}`),
  );

  // Los links propios del productor, igual que en la portada y también después
  // de las secciones: el catálogo es de genética, así que lo primero del menú
  // tiene que ser la genética. El armado vive en `utils/menuCatalogo.js` —lo
  // comparten esta pantalla y la portada, que antes tenían una copia cada una—
  // y ahí está la lista blanca de esquemas de URL. Acá un `#ancla` NAVEGA a la
  // portada con el hash puesto: esas anclas viven allá, y un `scrollIntoView`
  // desde la ficha busca un elemento que en esta página no existe.
  navSecciones.push(...linksDelProductor(
    config,
    (hash) => navigate(`/genetica/${slug}${hash}`),
  ));

  // "Volver al catálogo" va SOLO en la cabecera. En el pie seria una fila de mas
  // que no agrega nada: ahi el "Inicio" ya navega al catalogo —porque esta
  // pantalla le pasa `rutaInicio`— asi que la columna Navegacion mostraba dos
  // items al MISMO destino. Es el mismo criterio con el que
  // `seccionesDelCatalogo` saco el "Ver <seccion>" que repetia a su padre.
  const navHeader = [
    { key: 'volver', label: 'Volver al catálogo', onClick: () => navigate(`/genetica/${slug}`) },
    ...navSecciones,
  ];
  const navPie = navSecciones;

  // LAS FOTOS QUE PUEDE ABRIR EL VISOR, en el MISMO orden en que las dibuja el
  // carrusel: primero las propias del animal y despues las de los familiares.
  //
  // El visor recibia solo `animal.imagenes`, y ahi estaba el bug que se veia:
  // los nueve animales publicados tienen UNA sola foto propia y hasta cinco de
  // familiares, asi que "Ver en grande" abria un visor de UN item —sin flechas
  // para moverse— y mostraba la foto propia aunque el comprador estuviera
  // mirando la del padre.
  //
  // El `.filter(f => f.foto)` NO es opcional y tiene que ser el MISMO que hace
  // el carrusel (`famConFoto`): el backend manda todos los familiares
  // vinculados, con `foto` en null si no tienen. Si el visor incluyera a los
  // que no tienen foto, sus indices se corren respecto de los slides y volves a
  // abrir la imagen equivocada, que es justo lo que esto viene a arreglar.
  const familiaresConFoto = (animal.familiares || []).filter((f) => f.foto);
  const fotosDelVisor = [
    ...(animal.imagenes || []).map((i) => fixUrl(i.url)),
    ...familiaresConFoto.map((f) => fixUrl(f.foto)),
  ];

  const medidas = Object.entries(animal.medidas || {})
    .sort(([a], [b]) => posicionMedida(a) - posicionMedida(b));

  // Los datos de identificación se arman en una LISTA y se FILTRAN antes de
  // pintar, no se escriben uno por uno en la grilla. `Dato` ya devuelve null
  // cuando el valor está vacío, pero el `<Col xs={12}>` que lo envuelve sigue
  // ocupando su media fila: el hueco no se ve, pero CORRE todo lo que sigue a
  // la otra columna. Con 6 datos fijos y `cantidad_crias` sin cargar ya pasaba;
  // con 8 —`adn_animal` y `cabeza` no vienen en todos los animales— se nota.
  // ABA y HBA son padrones de asociaciones DISTINTAS, y el titulo se arma con
  // lo que hay en vez de ser fijo. Dos razones:
  //
  // 1. Casi siempre hay uno solo —639 de 641 animales— pero SI existen los dos
  //    a la vez, y uno de esos dos esta publicado hoy. Con `aba || hba` se le
  //    perdia el HBA en silencio.
  // 2. Un numero suelto debajo de un titulo fijo "ABA/HBA" no le dice al
  //    comprador cual de los dos padrones esta mirando, y es un dato que cruza
  //    contra la asociacion.
  //
  // Con los dos, se leen en el orden que anuncia el titulo.
  //
  // Se usa truthy y no `!= null` a proposito, al reves que el resto de la
  // ficha: `aba` es Integer y un padron numero CERO no es un numero de padron,
  // es un dato que no esta. Es la misma excepcion que `cantidad_crias`, y por
  // eso va dicha en vez de quedar como una casualidad que funciona.
  const hbaAnimal = (animal.hba_animal || '').trim() || null;
  const [tituloPadron, valorPadron] =
    animal.aba && hbaAnimal ? ['ABA/HBA', `${animal.aba} / ${hbaAnimal}`]
      : animal.aba ? ['ABA', String(animal.aba)]
        : ['HBA', hbaAnimal];

  const identificacion = [
    // El orden sigue el mockup y no es cosmetico: arranca por lo que
    // identifica al animal (nacimiento, RP), sigue por su papeleria (registro,
    // ADN, padron) y cierra con de quien es. Un comprador lee de arriba abajo.
    ['Nacimiento', animal.fecha_nac
      ? new Date(`${animal.fecha_nac}T00:00:00`).toLocaleDateString('es-AR')
      : null],
    ['RP', animal.rp_animal],
    // REGISTRO Y PADRON, SEPARADOS. Antes iban juntos —`2753032 (D)`— y era
    // mejor que la letra sola, pero son dos cosas distintas: el registro es la
    // CALIDAD del pedigri (Definitivo, Avanzado…) y el ABA/HBA es el NUMERO en
    // el padron de la asociacion. El comprador los usa para cosas distintas.
    // La palabra la manda el backend ya traducida (`registro_texto`), porque el
    // mismo mapa lo usa el PDF de caratulas.
    ['Registro', animal.registro_texto],
    ['Cabeza', animal.cabeza],
    ['ADN', animal.adn_animal],
    [tituloPadron, valorPadron],
    // El propietario es la cabaña, y la copropiedad se suma cuando existe: en
    // genetica de alto valor es informacion contractual, no un adorno. Hoy
    // `co_propietario` esta vacio en todos, asi que arranca mostrando solo la
    // cabaña — y por eso se compone con filter y no concatenando a ciegas.
    ['Centro genético', animal.centro_genetico],
    ['Propietario', [animal.cabana, animal.co_propietario].filter(Boolean).join(' y ')],
    ['Categoría', animal.nom_categoria],
    // CRIAS ES LA EXCEPCION, y va con truthy a proposito. En los DEPs y en las
    // medidas un 0 es un dato REAL —la linea base de la raza, un peso medido—,
    // pero `cantidad_crias` es un dato CARGADO A MANO: las crias reales
    // incluyen animales que nunca pasaron por Puja, asi que no se deriva ni con
    // la genealogia completa. Un "0 crias" AFIRMA que no tuvo, cuando lo que
    // pasa es que no se cargo — y eso, en la ficha de un toro que se vende,
    // habla mal del animal a partir de un dato faltante.
    //
    // La tarjeta de la portada ya lo hacia asi, con su comentario. Esta ficha
    // decia lo contrario, tambien con comentario asertivo: dos criterios
    // opuestos para el MISMO campo, los dos afirmando tener razon.
    ['Crías', animal.cantidad_crias || null],
    // Para todo lo demas, `!= null`: ahi un 0 si es un dato.
  ].filter(([, valor]) => valor != null && valor !== '');

  // LOS FAMILIARES YA NO TIENEN SECCION PROPIA: salen en el carrusel de medios,
  // con su caption —nombre, vínculo y premio—, así que repetirlos abajo era
  // mostrar dos veces lo mismo. En ese lugar van los OTROS animales de la
  // sección, que es lo que la ficha no tenía: sin ellos, ver otro toro obliga a
  // volver a la portada o a pasar de a uno con las flechas.
  const otros = data.otros || [];

  // LA COLUMNA DE INFORMACION, EN UNA CONSTANTE PORQUE SE DIBUJA DOS VECES.
  //
  // En escritorio va a la derecha del carrusel; en mobile va DEBAJO de el, y en
  // el medio —entre el carrusel y estas tarjetas— no puede meterse nada. Se
  // resuelve como en la ficha del remate (`AnimalPage.jsx`): la columna derecha
  // es `xs={0}` —no existe en mobile— y la misma constante se dibuja otra vez
  // dentro de un `div` que solo es visible por debajo de 992px.
  //
  // Un elemento JSX es una DESCRIPCION, no una instancia: usar la misma
  // constante en dos lugares crea dos nodos distintos, no comparte estado.
  const columnaInfo = (
    <>
      <div className="pj-ficha-pieza" style={{ padding: 24 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          justifyContent: 'space-between', marginBottom: 10,
        }}>
          <Space size={8} wrap>
            {animal.nom_raza && (
              /* LA MISMA FORMA QUE LOS DEMAS CHIPS: radio 4, sin borde,
                 fondo tenue del acento. Era una pildora de radio 999 y quedaba
                 como una tercera manera de contener un dato corto en la misma
                 pantalla.

                 SIN MAYUSCULAS, a diferencia del chip de rank: esas quedan
                 reservadas para ESE, que es el unico que grita un lugar en la
                 raza. Si gritan todos, no grita ninguno — el mismo criterio con
                 el que la fila de DEPs destaca solo el escalon de arriba. */
              <span
                style={{
                  fontSize: 11.5, fontWeight: 700, letterSpacing: '.03em',
                  background: `${acento}14`, color: acento, borderRadius: 4,
                  padding: '2px 8px',
                }}
              >
                {animal.nom_raza}
              </span>
            )}
            {animal.apto_comercializar ? <Tag color="green">Disponible</Tag> : null}
          </Space>
          {/* PILL VERDE CON TEXTO E ICONO EN BLANCO, igual que el de
              la ficha del remate (`AnimalPage.jsx`). Antes era un
              `type="text"` con la letra verde: se leia como un link y
              no como el atajo que es.

              SIN `type="text"`: ese tipo pinta el fondo en hover con su
              propio color y se comia el verde. */}
          <Button
            size="small"
            icon={<WhatsAppOutlined />}
            onClick={compartir}
            style={{
              background: VERDE_WHATSAPP,
              border: 'none',
              // '#fff' literal y NO `T.surface`: esto es el color del TEXTO
              // sobre el verde de WhatsApp, no una superficie del tema. Atarlo
              // al token haria que cambiar el fondo de las tarjetas cambiara la
              // letra de este boton.
              color: '#fff',
              borderRadius: 32,
              padding: '4px 14px',
              height: 'auto',
              lineHeight: 1.6,
              fontFamily: tipografia.body,
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: '.02em',
              flexShrink: 0,
            }}
          >
            COMPARTIR
          </Button>
        </div>

        {/* El SOBRENOMBRE, que es con el que se lo conoce. */}
        <Title
          level={2}
          style={{
            fontFamily: tipografia.title,
            color: config.color_title || '#1a1a1a',
            fontWeight: 900,
            letterSpacing: 1,
            margin: '0 0 6px',
          }}
        >
          {animal.nombre_corto}
        </Title>

        {/* El de pedigrí, sólo si es distinto: repetirlo sería ruido. Y
            va porque es el nombre con el que el animal figura en la
            asociación — el comprador que cruza datos lo necesita. */}
        {animal.nom_animal !== animal.nombre_corto && (
          <div
            style={{
              fontSize: 17,
              fontWeight: 900,
              letterSpacing: '0.5px',
              marginBottom: 16,
              color: T.textDim,
              lineHeight: 1.3,
            }}
          >
            {animal.nom_animal}
          </div>
        )}

        <Row gutter={[16, 0]}>
          {identificacion.map(([etiqueta, valor]) => (
            <Col xs={12} key={etiqueta}>
              <Dato etiqueta={etiqueta} valor={valor} />
            </Col>
          ))}
        </Row>

        {/* EL PREMIO CIERRA LA TARJETA, no la encabeza. Arriba, mezclado
            con la raza y el "Disponible", competia con el nombre del
            animal, que es lo primero que hay que leer. Abajo cierra la
            ficha con el argumento de venta, que es donde lo puso Facu.
            El trofeo lo separa de las etiquetas de estado: un premio no
            es lo mismo que "esta disponible". */}
        {animal.premio && (
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              marginTop: 14, paddingTop: 14,
              borderTop: `1px solid ${T.border}`,
              fontFamily: tipografia.body,
              color: config.color_paragraph || '#666',
              fontSize: 14, lineHeight: 1.4,
            }}
          >
            <TrophyOutlined style={{ color: acento, fontSize: 16, flexShrink: 0 }} />
            <span>{animal.premio}</span>
          </div>
        )}

        {animal.mensaje && (
          <Paragraph
            style={{
              fontFamily: tipografia.body,
              color: config.color_paragraph || '#666',
              fontSize: 16, lineHeight: 1.6, marginTop: 4,
            }}
          >
            {animal.mensaje}
          </Paragraph>
        )}

        {/* CANAL WHATSAPP: verde, como el resto del sitio. CANAL INTERNA: el
            acento del productor — acá el botón no representa a WhatsApp, así
            que no le corresponde su verde fijo. Sin número Y sin canal
            interna no hay a dónde mandar la consulta, así que no se dibuja —
            igual que antes. */}
        {mostrarConsultar && (
          <Button
            type="primary"
            size="large"
            icon={canalConsultas === 'whatsapp' ? <WhatsAppOutlined /> : undefined}
            block
            style={
              canalConsultas === 'whatsapp'
                ? { background: VERDE_WHATSAPP, borderColor: VERDE_WHATSAPP, marginTop: 8 }
                : { background: acento, borderColor: acento, marginTop: 8 }
            }
            onClick={() => setModalConsultaAbierto(true)}
          >
            Consultar por este animal
          </Button>
        )}
      </div>

      {/* ── Medidas, DEBAJO de la tarjeta principal ──────────────── */}
      {medidas.length > 0 && (
        <div className="pj-ficha-pieza" style={{ padding: 20, marginTop: 24 }}>
          <Title level={5} style={{ fontFamily: tipografia.title, marginTop: 0 }}>
            Medidas
          </Title>
          {/* En DOS columnas. Y son las medidas del animal, distintas de
              los DEPs: los DEPs predicen la progenie, esto es lo que se
              le midió a él. */}
          <Row gutter={[16, 12]}>
            {medidas.map(([etiqueta, valor]) => (
              <Col xs={12} key={etiqueta}>
                <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1 }}>{valor}</div>
                <div
                  style={{
                    fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em',
                    textTransform: 'uppercase', color: T.textDim, marginTop: 3,
                  }}
                >
                  {etiqueta}
                </div>
              </Col>
            ))}
          </Row>

          {/* LAS CARACTERISTICAS DESTACADAS cierran la tarjeta de
              medidas, que es donde las puso Facu.
              Son texto libre del productor —"Facilidad de parto",
              "Nueva incorporacion", "Apto vaquillonas 18 meses"— y por
              eso van como chips y no como filas: no son un dato medido
              con su unidad, son argumentos de venta. */}
          {(animal.caracteristicas || []).length > 0 && (
            <Space size={[8, 8]} wrap style={{ marginTop: 16 }}>
              {animal.caracteristicas.map((c) => (
                /* MISMA FORMA DE CONTENER QUE EL CHIP DE RANK, y a proposito:
                   la ficha tenia DOS maneras distintas de envolver un dato
                   corto —esta pildora de radio 999 con borde, y el chip de rank
                   de radio 4 sin borde— y el ojo lee dos formas como dos
                   CATEGORIAS de informacion que en realidad no existen.

                   Se alinea al del rank en la FORMA: radio 4, sin borde,
                   fondo tenue del acento, 11.5px. El padding horizontal es
                   mayor porque estos textos son frases —"Apto vaquillonas 18
                   meses"— y no un numero de tres caracteres.

                   PERO SIN MAYUSCULAS. Quedan solo para el chip de rank: es el
                   unico que grita un lugar en la raza, y si gritan todos no
                   grita ninguno. Ademas una frase larga entera en mayusculas se
                   lee peor que en minusculas. */
                <Tag
                  key={c}
                  style={{
                    fontFamily: tipografia.body,
                    fontSize: 11.5,
                    fontWeight: 700,
                    letterSpacing: '.03em',
                    borderRadius: 4,
                    padding: '2px 8px',
                    background: `${acento}14`,
                    color: acento,
                    border: 'none',
                    margin: 0,
                  }}
                >
                  {c}
                </Tag>
              ))}
            </Space>
          )}
        </div>
      )}
    </>
  );

  return (
    <div className="micrositio-page" style={{ background: T.surface, minHeight: '100vh' }}>
      <style>{ESTILOS}</style>

      {conCabecera && (
        <SiteHeader
          config={config}
          tipografia={tipografia}
          logoUrl={logoUrl}
          evento={comoEvento}
          scrolled={scrolled}
          isAuthenticated={false}
          slug={slug}
          navPropia={navHeader}
          rutaInicio={`/genetica/${slug}`}
          franjaSuperior={franjaBienvenida}
        />
      )}

      <div style={{ paddingTop: conCabecera ? altoHeader : 0 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 24px 64px' }}>

          {/* ── Navegación ─────────────────────────────────────────────── */}
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12, flexWrap: 'wrap', marginBottom: 24,
            }}
          >
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(`/genetica/${slug}`)}
            >
              Catálogo
            </Button>
            <Space>
              {/* Se ocultan en los extremos en vez de quedar grises: así se ve
                  dónde empieza y dónde termina la sección. */}
              {anterior && (
                <Button icon={<LeftOutlined />} onClick={() => irAnimal(anterior)}>
                  {anterior.nombre}
                </Button>
              )}
              {siguiente && (
                <Button onClick={() => irAnimal(siguiente)}>
                  {siguiente.nombre} <RightOutlined />
                </Button>
              )}
            </Space>
          </div>

          <Row gutter={[24, 24]}>
            {/* ── Fotos ──────────────────────────────────────────────────── */}
            <Col xs={24} lg={13}>
              {/* EL MISMO carrusel del micrositio de remate: video primero,
                  después la foto principal, después las de los familiares, con
                  navegación por miniaturas. Se importa y no se copia, igual que
                  el árbol genealógico.

                  Las claves se adaptan acá y no en el backend: el contrato
                  público del catálogo (`url`, `foto`) está bien nombrado para
                  lo que es, y renombrarlo para que encaje con un componente
                  ataría la API a la forma interna de una pantalla. */}
              <div style={{ position: 'relative' }}>
              {/* SIN la prop `premio`: desde que el premio cierra la tarjeta
                  de identidad, pasarlo tambien aca lo pinta de caption sobre
                  CADA foto y queda repetido en la misma pantalla. Mismo
                  criterio con el que este archivo saco el "Volver al catalogo"
                  duplicado del pie y la seccion propia de familiares. */}
              <CarruselMedia
                videoUrl={animal.codigo_video}
                // `url_thumb` va aparte y NO es lo mismo que `url_web`:
                // `CarruselMedia` lee `img.url_thumb || img.url_imagen` para la
                // tira de miniaturas. Mapeando sólo la grande, cada miniatura
                // se bajaba la foto a tamaño completo — y en un catálogo de
                // genética la foto ES el argumento, así que es justo donde no
                // se puede tirar ancho de banda. El backend ya manda `thumb`.
                onCambioFoto={(i) => { if (i != null) setFotoActual(i); }}
                imagenes={animal.imagenes.map((i) => ({
                  url_web: i.url, url_imagen: i.url, url_thumb: i.thumb,
                }))}
                familiares={animal.familiares.map((f) => ({
                  url_foto_familiar: f.foto,
                  nom_familiar: f.nombre,
                  vinculo_familiar: f.vinculo,
                  premio_familiar: f.premio,
                }))}
                config={config}
              />

              {/* El botón va ARRIBA A LA DERECHA y no sobre el centro: las
                  miniaturas y las flechas del carrusel viven abajo y al medio.
                  Sólo con fotos — el video no se abre en el visor, y un botón
                  "ampliar" sobre un animal sin fotos no amplía nada. */}
              {/* Con `fotosDelVisor` y no con `animal.imagenes`: un animal sin
                  foto propia pero con fotos de familiares tiene algo que
                  ampliar. Sigue sin dibujarse cuando no hay NINGUNA — un
                  "ampliar" sobre un animal sin fotos no amplia nada. */}
              {fotosDelVisor.length > 0 && (
                <Button
                  icon={<ExpandOutlined />}
                  onClick={() => {
                    // El copiado va ACA y no en un efecto: abrir es el unico
                    // momento en que los dos indices tienen que coincidir.
                    setFotoVisor(fotoActual);
                    setVerFotos(true);
                  }}
                  style={{
                    position: 'absolute', top: 12, right: 12, zIndex: 2,
                    background: 'rgba(255,255,255,.92)', border: 'none',
                    fontWeight: 600, fontSize: 12,
                  }}
                >
                  Ver en grande
                </Button>
              )}

              {/* Sin hijos: la lista va por `items`. Es lo que permite tener el
                  visor sin que el carrusel sepa que existe. */}
              <Image.PreviewGroup
                items={fotosDelVisor}
                preview={{
                  visible: verFotos,
                  onVisibleChange: setVerFotos,
                  // Sin esto el visor abria SIEMPRE la foto 1: el comprador
                  // estaba mirando la cuarta, tocaba "Ver en grande" y volvia
                  // al principio. En un catalogo de genetica, donde la foto ES
                  // el argumento, es el momento donde menos se puede fallar.
                  current: fotoVisor,
                  // Y `onChange` NO es opcional cuando se pasa `current`.
                  // `fotoVisor` es un numero y nunca vale `undefined`, asi que
                  // el `useMergedState` de rc-util deja la prop CONTROLADA para
                  // siempre: adentro del visor la flecha escribe el estado
                  // interno y el render siguiente lo pisa con el valor de
                  // afuera. Sin esta linea el visor abria en la foto correcta y
                  // despues no se podia mover de ahi.
                  //
                  // Escribe en `fotoVisor` y NO en `fotoActual`: navegar adentro
                  // del visor no tiene por que mover el carrusel de atras.
                  onChange: setFotoVisor,
                }}
              />
              </div>

              {/* EL TEXTO DESCRIPTIVO, debajo del carrusel: es el lugar que
                  marco Facu en el mockup. Va en la columna de la FOTO y no en
                  la tarjeta de identidad porque acompaña a la imagen — el
                  comprador lee la foto y despues el relato del animal.
                  Distinto de `mensaje`, que vive en la tarjeta de la derecha y
                  es el anuncio comercial de condicion de venta. Dos textos con
                  dos trabajos: uno cuenta el animal, el otro dice como se
                  vende. */}
              {animal.descripcion && (
                /* LA BARRA A LA IZQUIERDA ES EL ACENTO DEL PRODUCTOR, no un
                   color decidido acá. La forma sale del bloque de aviso del
                   artifact —barra de 3px, borde tenue, esquinas de 12 y una
                   sombra mínima—, pero la PALETA es la que el productor eligió
                   al crear su catálogo (`config.color_primary`). Traer también
                   sus colores sería pintar la vidriera de una cabaña con la
                   marca de otro.

                   El fondo va en `${acento}0a` y no blanco: sobre la hoja
                   blanca de la ficha un bloque blanco con borde se lee como una
                   tarjeta más, y esto no es una tarjeta — es el párrafo que
                   cuenta al animal, apenas levantado del fondo. */
                <div
                  style={{
                    background: `${acento}0a`,
                    border: `1px solid ${T.border}`,
                    borderLeft: `3px solid ${acento}`,
                    // T.radius y T.shadowSm, NO el 12 y la string a mano.
                    // `micrositioTema.js` existe porque este archivo ya habia
                    // hardcodeado la sombra una vez; repetirlo en el mismo
                    // commit que corrige el #fff de arriba seria dejar las dos
                    // convenciones conviviendo otra vez.
                    borderRadius: T.radius,
                    padding: '16px 18px',
                    // 24, IGUAL QUE EL RESTO. Cuando esto eran dos Rows, el
                    // `gutter={[24, 24]}` separaba los bloques solo; al pasar
                    // todo a una columna esa separacion se perdio y las piezas
                    // quedaron pegadas una abajo de la otra. Se repone a mano,
                    // con el MISMO numero, para que la pagina siga teniendo un
                    // solo ritmo vertical.
                    marginTop: 24,
                    boxShadow: T.shadowSm,
                  }}
                >
                  <Paragraph
                    style={{
                      fontFamily: tipografia.body,
                      color: config.color_paragraph || '#666',
                      fontSize: 15, lineHeight: 1.7, margin: 0,
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {animal.descripcion}
                  </Paragraph>
                </div>
              )}

              {/* LAS TARJETAS, SOLO EN MOBILE. La columna de al lado es
                  `xs={0}`, asi que por debajo de 992px esta es la unica copia
                  que existe: no hay contenido duplicado a la vista. */}
              <div className="pj-info-mobile" style={{ marginTop: 24 }}>
                {columnaInfo}
              </div>

              {/* ── DEPs, JUSTO DEBAJO DE LA DESCRIPCION ─────────────────────
                  En escritorio quedan bajo el texto que cuenta al animal, que
                  es el orden en que se lee: primero que ES, despues cuanto
                  MIDE su progenie. En mobile caen despues de las tarjetas de
                  arriba, porque el `div` de mobile los precede.

                  LA GUARDA VA PEGADA AL BLOQUE. Antes vivia afuera, en el
                  <Col> de la fila de abajo, y al mudar el contenido se quedo
                  sin ella: un animal sin DEPs dibujaba la tarjeta con el
                  titulo y nada adentro. */}
              {animal.deps.length > 0 && (
                <div className="pj-ficha-pieza" style={{ padding: 20, marginTop: 24 }}>
                  <Title level={4} style={{ fontFamily: tipografia.title, marginTop: 0 }}>
                    DEPs
                    {animal.deps_evaluacion_anio && (
                      <Text type="secondary" style={{ fontSize: 13, marginLeft: 10, fontWeight: 400 }}>
                        Sumario {animal.deps_evaluacion_anio}
                      </Text>
                    )}
                  </Title>
                  {animal.deps.map((dep) => (
                    <FilaDep key={dep.clave} dep={dep} color={acento} />
                  ))}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    El percentil es el lugar del animal dentro de su raza: 1 es el mejor.
                  </Text>
                </div>
              )}

              {/* ── Arbol genealogico ─────────────────────────────────────
                  SIN envoltorio propio: `ArbolGenealogico` ya viene dentro de
                  su Card, con su título "Árbol Genealógico". Envolverlo daba
                  una tarjeta adentro de otra y dos títulos para lo mismo.
                  Queda el suyo. */}
              {hayGenealogia && (
                // El envoltorio existe SOLO para separar: `ArbolGenealogico`
                // trae su propia Card y no recibe `style`, asi que el margen
                // no tiene donde apoyarse sin este div.
                <div style={{ marginTop: 24 }}>
                  <ArbolGenealogico
                    genealogia={g}
                    config={config}
                    tipografia={tipografia}
                  />
                </div>
              )}
            </Col>

            {/* ── Identidad, TODA dentro de una tarjeta ──────────────────── */}
            <Col xs={0} lg={11}>
              {columnaInfo}
            </Col>
          </Row>



          {/* ── Los otros de esta sección ───────────────────────────── */}
          {otros.length > 0 && (
            <div style={{ marginTop: 32 }}>
              <Title level={4} style={{ fontFamily: tipografia.title }}>
                {/* El título sale del que configuró el productor: si le puso
                    "Nuestros padres", acá no puede decir "Otros toros". */}
                Más de {(data.seccion?.titulo || 'el catálogo').toLowerCase()}
              </Title>
              {/* 4 por fila en desktop y no 6: con `lg={4}` entraban tres en
                  media pantalla y la foto quedaba del tamaño de un sello — en
                  una vidriera de genética la foto ES el argumento. */}
              <Row gutter={[16, 16]}>
                {otros.map((o) => (
                  <Col xs={12} md={8} lg={6} key={o.id_animal}>
                    <a
                      href={`/genetica/${slug}/animal/${o.slug}`}
                      // El `href` se queda —ctrl+click, botón del medio, click
                      // derecho, buscadores— y se intercepta sólo el click
                      // simple, que si no recarga la app entera y reinicia el
                      // handshake de alto del iframe. Ver `navegacionSpa.js`.
                      onClick={clickInterno(navigate, `/genetica/${slug}/animal/${o.slug}`)}
                      className="pj-ficha-pieza"
                      style={{ display: 'block', height: '100%', color: 'inherit' }}
                    >
                      <div style={{ aspectRatio: '4 / 3', background: T.pearl }}>
                        {o.imagen && (
                          <img
                            src={fixUrl(o.imagen)}
                            alt={o.nombre_corto || ''}
                            loading="lazy"
                            style={{
                              width: '100%', height: '100%',
                              objectFit: 'cover', display: 'block',
                            }}
                          />
                        )}
                      </div>
                      <div style={{ padding: 12 }}>
                        <div style={{ fontWeight: 700, fontSize: 13.5, lineHeight: 1.3 }}>
                          {o.nombre_corto}
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {o.nom_categoria || ''}
                        </Text>
                        {o.apto_comercializar ? (
                          <div
                            style={{
                              fontSize: 11, fontWeight: 700,
                              color: T.success, marginTop: 4,
                            }}
                          >
                            ● Disponible
                          </div>
                        ) : null}
                      </div>
                    </a>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </div>
      </div>

      {conPie && (
        <SiteFooter
          config={config}
          tipografia={tipografia}
          evento={comoEvento}
          categorias={[]}
          logoUrl={logoUrl}
          slug={slug}
          navPropia={navPie}
          rutaInicio={`/genetica/${slug}`}
        />
      )}

      {/* Modal de "Consultar por este animal". `destroyOnHidden` para que el
          form arranque limpio la próxima vez que se abra —si no, reabrir
          mostraba los datos de la consulta anterior. */}
      <Modal
        title={`Consultar por ${animal.nombre_corto}`}
        open={modalConsultaAbierto}
        onCancel={cerrarModalConsulta}
        footer={null}
        destroyOnHidden
      >
        {consultaEnviada ? (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <CheckCircleFilled style={{ fontSize: 40, color: VERDE_WHATSAPP, marginBottom: 12 }} />
            {consultaEnviada.canal === 'interna' ? (
              <Paragraph style={{ fontSize: 15, marginBottom: 20 }}>
                Recibimos tu consulta. {data.catalogo.nombre} se va a comunicar con vos.
              </Paragraph>
            ) : (
              <>
                <Paragraph style={{ fontSize: 15, marginBottom: 16 }}>
                  Tu consulta quedó registrada. Si WhatsApp no se abrió solo, tocá el botón.
                </Paragraph>
                {/* Un <a> normal y no otro `window.open`: si el navegador
                    bloqueó el automático, el click del comprador sobre este
                    link SÍ cuenta como gesto propio, así que abre sin
                    problema. */}
                <a
                  href={consultaEnviada.urlWhatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    type="primary"
                    icon={<WhatsAppOutlined />}
                    style={{ background: VERDE_WHATSAPP, borderColor: VERDE_WHATSAPP }}
                  >
                    Abrir WhatsApp
                  </Button>
                </a>
              </>
            )}
            <div style={{ marginTop: 20 }}>
              <Button onClick={cerrarModalConsulta}>Cerrar</Button>
            </div>
          </div>
        ) : (
          <Form form={formConsulta} layout="vertical" onFinish={enviarConsulta}>
            <Form.Item
              name="nombre"
              label="Nombre"
              rules={[
                { required: true, message: 'Ingresá tu nombre' },
                { min: 2, max: 120, message: 'Entre 2 y 120 caracteres' },
              ]}
            >
              <Input placeholder="Tu nombre" />
            </Form.Item>
            <Form.Item
              name="telefono"
              label="Teléfono"
              // El backend lo lleva a formato internacional (549 + área +
              // número) para que el botón de WhatsApp del productor funcione.
              // Lo que NO puede arreglar solo es el "15" en el medio, porque
              // no sabe cuántos dígitos tiene el área: por eso se pide así.
              extra="Con código de área, sin el 0 ni el 15"
              rules={[
                { required: true, message: 'Ingresá tu teléfono' },
                {
                  // Validación SUAVE a propósito: no se exige un formato de
                  // teléfono argentino, sólo que haya dígitos suficientes
                  // para que del otro lado se pueda llamar o mandar WhatsApp.
                  validator: (_, value) => {
                    const digitos = (value || '').replace(/\D/g, '');
                    return digitos.length >= 8
                      ? Promise.resolve()
                      : Promise.reject(new Error('Ingresá un teléfono válido'));
                  },
                },
              ]}
            >
              <Input placeholder="Ej: 3764123456" />
            </Form.Item>
            <Form.Item name="mensaje" label="Mensaje (opcional)">
              <Input.TextArea
                placeholder="Contanos qué querés saber de este animal"
                rows={4}
                maxLength={1000}
                showCount
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={enviandoConsulta}
                icon={canalConsultas === 'whatsapp' ? <WhatsAppOutlined /> : undefined}
                style={
                  canalConsultas === 'whatsapp'
                    ? { background: VERDE_WHATSAPP, borderColor: VERDE_WHATSAPP }
                    : { background: acento, borderColor: acento }
                }
              >
                Enviar consulta
              </Button>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default AnimalCatalogoPage;
